const typeWriterAnimation = (element, text, speed = 20) => {
  return new Promise((resolve) => {
    let i = 0;
    const typeWriter = () => {
      if (i < text.length) {
        element.innerHTML += text.charAt(i);
        i++;
        setTimeout(typeWriter, speed);
      } else {
        resolve();
      }
    };
    element.innerHTML = ''; // Clear existing content
    typeWriter();
  });
};

const parseApplicantData = (data) => {
  try {
    const applicantMatches = [...data.matchAll(/<applicant><hash>([^<]+)<\/hash><result>([^<]+)<\/result><\/applicant>/g)];
    return applicantMatches.map(match => ({
      application_hash: match[1].replace(/"/g, ''),
      result: match[2]
    })) || null;
  } catch (error) {
    console.error('Error parsing applicant data:', error);
    return null;
  }
};

const replaceAiResponseRow = async (application) => {
  const rowId = `ai-response-row-${application.application_hash}`;
  const textElement = document.getElementById(`${rowId}-text`);

  if (textElement) {
    await typeWriterAnimation(textElement, application.result);
  } else {
    console.error(`Text element not found for application hash: ${application.application_hash}`);
  }
};

const createAnimatedAdditionalContent = async (application, totalColumns) => {
  const rowId = `ai-response-row-${application.application_hash}`;
  const additionalContent = `
    <tr class="ai-response-row-${application.application_hash} ai-response-row" data-hash="${application.application_hash}" data-ai-insight-loading="true">
      <td colspan="${totalColumns}">
        <div>
          <span class="icon-svg-stars-01"></span>
          <span id="${rowId}-text"></span>
        </div>
      </td>
    </tr>
  `;

  const existingRow = document.querySelector(`.row-${application.application_hash}`);
  if (existingRow) {
    existingRow.insertAdjacentHTML('afterend', additionalContent);
  } else {
    console.error(`Existing row not found for application hash: ${application.application_hash}`);
    return;
  }

  const textElement = document.getElementById(`${rowId}-text`);
  if (textElement) {
    await typeWriterAnimation(textElement, application.result);
  } else {
    console.error(`Text element not found for application hash: ${application.application_hash}`);
  }
};

/**
 * Conversational AI may wrap the visible reply in JSON `{ "message": "..." }` (websocket and persisted history).
 * @param {string} content - Raw content from API or websocket
 * @returns {string}
 */
const parseConversationalAiMessageContent = (content) => {
  return content;
  /* TODO: Add back in when we have a way to parse the JSON object from the content */
  if (content == null || content === '') return content;
  if (typeof content === 'object' && content !== null && content.message != null) {
    return content.message;
  }
  if (typeof content !== 'string') return content;
  let message = content;
  try {
    const parsed = JSON.parse(content);
    if (parsed?.message != null) message = parsed.message;
  } catch (_) {
    /* plain markdown/text */
  }
  return message;
};

// Returns { content, annotations } for Conv UI WS payloads, handling the citation-wrapper shape.
const parseConversationalAiPayload = (data) => {
  if (data && typeof data === 'object' && typeof data.message === 'string') {
    return {
      content: data.message,
      annotations: Array.isArray(data.annotations) ? data.annotations : [],
    };
  }
  if (typeof data === 'string') {
    return { content: data, annotations: [] };
  }
  return { content: String(data ?? ''), annotations: [] };
};

const hostnameOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};

const escapeHtmlAttr = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const safeHref = (url) => {
  try {
    const u = new URL(url);
    return ['http:', 'https:'].includes(u.protocol) ? u.toString() : null;
  } catch { return null; }
};

// Number annotations by URL identity so inline pills and the Sources list stay aligned:
// every annotation that points at the same URL gets the same number.
const numberAnnotationsByUrl = (annotations) => {
  const urlToNumber = new Map();
  (annotations || []).forEach((a) => {
    const url = a?.url_citation?.url;
    if (url && !urlToNumber.has(url)) {
      urlToNumber.set(url, urlToNumber.size + 1);
    }
  });
  return urlToNumber;
};

// Insert clickable pill markers at end_index (already snapped server-side).
// Sort tiebreak (b.n - a.n) ensures same-position markers read "1 2 3" left-to-right.
const renderTextWithFootnotes = (text, annotations) => {
  if (!text || !annotations || annotations.length === 0) return text;
  const urlToNumber = numberAnnotationsByUrl(annotations);
  const enriched = annotations
    .map((a) => {
      const c = a?.url_citation || {};
      return {
        n: urlToNumber.get(c.url) || 0,
        end: typeof c.end_index === 'number' ? c.end_index : null,
        url: c.url || '',
        tooltip: c.cited_text || c.title || '',
      };
    })
    .filter(({ end, n }) => n > 0 && end !== null && end >= 0 && end <= text.length)
    .sort((a, b) => b.end - a.end || b.n - a.n);

  // Skip back-to-back markers for the same source at the same position
  // (e.g. three snippets from the same URL citing the same sentence → one pill).
  let result = text;
  let lastEnd = null;
  let lastN = null;
  for (const { n, end, url, tooltip } of enriched) {
    if (end === lastEnd && n === lastN) continue;
    lastEnd = end;
    lastN = n;
    const href = safeHref(url);
    const titleAttr = tooltip ? ` title="${escapeHtmlAttr(tooltip)}"` : '';
    const numHtml = `<span class="ai-citation-marker-num">${n}</span>`;
    const marker = href
      ? `<a class="ai-citation-marker" href="${escapeHtmlAttr(href)}" target="_blank" rel="noopener noreferrer"${titleAttr}>${numHtml}</a>`
      : `<span class="ai-citation-marker"${titleAttr}>${numHtml}</span>`;
    result = result.slice(0, end) + marker + result.slice(end);
  }
  return result;
};

// Dedupe sources by URL; preserve first-occurrence order for numbering.
// Numbering matches renderTextWithFootnotes so pill [N] ↔ source [N].
const buildSourcesList = (annotations) => {
  if (!annotations || annotations.length === 0) return [];
  const seen = new Map();
  annotations.forEach((a) => {
    const c = a?.url_citation;
    if (!c?.url) return;
    if (!seen.has(c.url)) {
      seen.set(c.url, {
        n: seen.size + 1,
        url: c.url,
        title: c.title || hostnameOf(c.url),
        cited_text: c.cited_text || '',
      });
    }
  });
  return Array.from(seen.values());
};

export {
  parseApplicantData,
  replaceAiResponseRow,
  createAnimatedAdditionalContent,
  parseConversationalAiMessageContent,
  parseConversationalAiPayload,
  hostnameOf,
  renderTextWithFootnotes,
  buildSourcesList,
};